// Import React hooks for state, effect, and refs
import { useState, useEffect, useRef } from 'react';
// Import React Router hooks for reading URL parameters and navigating programmatically
import { useParams, useNavigate } from 'react-router-dom';
// Import custom authentication hook to get the current user and loading state
import { useAuth } from '../hooks/useAuth';
// Import Axios client to make API requests
import axiosClient from '../api/axiosClient';

// -----------------------------
// TypeScript interface defining a chat message
// -----------------------------
interface ChatMessage {
  id: number;         // Unique ID of the message
  user: number;       // ID of the user who sent the message
  username: string;   // Username of the sender
  message: string;    // Message content
  timestamp: string;  // Timestamp when the message was sent
  room_name: string;  // Chat room identifier (slug of the course)
}
// -----------------------------
// Main ChatPage component
// -----------------------------
function ChatPage() {
    // Get the course slug from the URL parameters
    const {slug} = useParams<{ slug: string }>();
    // Get authenticated user and loading state from auth hook
    const {user, isLoading} = useAuth();
    // Hook for programmatic navigation (e.g., redirect to login)
    const navigate = useNavigate();
    // -----------------------------
    // React state variables
    // -----------------------------
    const [messages, setMessages] = useState<ChatMessage[]>([]); // Array of chat messages
    const [message, setMessage] = useState('');                  // Input field for new messages
    const [ws, setWs] = useState<WebSocket | null>(null);        // WebSocket instance for live chat
    const [courseName, setCourseName] = useState<string>('Chat');// Displayed course name
    const [error, setError] = useState<string | null>(null);     // Error message to display in UI
    const [isCollapsed, setIsCollapsed] = useState(false);       // UI toggle to collapse the chat window
    const [isSending, setIsSending] = useState(false);           // Prevent sending multiple messages at once
    // -----------------------------
    // Refs for mutable values that persist between renders
    // -----------------------------
    const messagesEndRef = useRef<HTMLDivElement>(null); // Ref to bottom of message list for auto-scroll
    const wsReconnectAttempts = useRef(0);               // Count of WebSocket reconnection attempts
    const maxReconnectAttempts = 5;                      // Maximum number of reconnection attempts
    const lastSentMessage = useRef<string | null>(null); // Cache of last sent message to prevent duplicates
    // -----------------------------
    // useEffect: handles auth check, course fetch, and WebSocket connection
    // -----------------------------
    useEffect(() => {
        // If auth is still loading, exit effect
        if (isLoading) return;
        // Redirect to login if no user or slug is present
        if (!user || !slug) {
            navigate('/login');
            return;
        }
        // -----------------------------
        // Fetch course details (name) from API
        // -----------------------------
        const fetchCourse = async () => {
            try {
                const response = await axiosClient.get('/elearning/api/courses/', {
                    withCredentials: true, // Include cookies/session
                    params: {slug},      // Filter courses by slug
                });
                // Find course by slug in response data
                const course = response.data.find((c: any) => c.slug === slug);
                if (course) {
                    setCourseName(course.name); // Update course name state
                } else {
                    setError('Course not found'); // Set error if course not found
                }
                // eslint-disable-next-line @typescript-eslint/no-unused-vars
            } catch (err: any) {
                setError('Failed to load course details'); // Set error if fetch fails
            }
        };
        fetchCourse(); // Execute course fetch
        // -----------------------------
        // WebSocket connection function
        // -----------------------------
        const connectWebSocket = () => {
            // Build WebSocket URL using course slug
            const wsUrl = `ws://localhost:8000/ws/chat/${slug}/`;
            // Open WebSocket connection
            const websocket = new WebSocket(wsUrl);
            setWs(websocket); // Save WebSocket in state
            // -----------------------------
            // WebSocket event handlers
            // -----------------------------
            // Event: connection opened
            websocket.onopen = () => {
                setError(null); // Clear previous errors
                wsReconnectAttempts.current = 0; // Reset reconnection attempts
            };
            // Event: message received from server
            websocket.onmessage = (event) => {
                const data = JSON.parse(event.data);
                // Case 1: initial batch of messages sent by server
                if (data.type === 'initial_messages') {
                    setMessages(
                        data.messages
                            .filter((msg: any) => msg.username && msg.message && msg.timestamp) // Validate messages
                            .map((msg: any) => ({
                                id: msg.id || 0,
                                user: msg.user || 0,
                                username: msg.username,
                                message: msg.message,
                                timestamp: msg.timestamp,
                                room_name: slug,
                            }))
                    );
                    // Case 2: new live message
                } else if (data.message && data.username && data.timestamp) {
                    const newMessage = {
                        id: data.id || 0,
                        user: data.user || 0,
                        username: data.username,
                        message: data.message,
                        timestamp: data.timestamp,
                        room_name: slug,
                    };
                    // Prevent duplicate messages from being added
                    if (lastSentMessage.current !== `${data.username}:${data.message}:${data.timestamp}`) {
                        setMessages((prev) => {
                            const updatedMessages = [...prev, newMessage];
                            // Scroll chat to bottom after new message is added
                            setTimeout(() => {
                                messagesEndRef.current?.scrollIntoView({behavior: 'smooth', block: 'end'});
                            }, 0);
                            return updatedMessages;
                        });
                        // Update last sent message ref
                        lastSentMessage.current = `${data.username}:${data.message}:${data.timestamp}`;
                    }
                }
            };
            // Event: WebSocket error occurred
            websocket.onerror = (error) => {
                console.error('ChatPage: WebSocket error=', error);
                setError('You are not enrolled in this course or are blocked.');
            };
            // Event: WebSocket closed
            websocket.onclose = (event) => {
                setWs(null); // Clear WebSocket from state
                // Handle specific close codes
                if (event.code === 4001) {
                    setError('You must be logged in to access chat.');
                    navigate('/login');
                } else if (event.code === 4002) {
                    setError('Failed to connect to chat. Retrying...');
                } else if (wsReconnectAttempts.current < maxReconnectAttempts) {
                    wsReconnectAttempts.current += 1;
                    setTimeout(connectWebSocket, 2000); // Retry connection after delay
                } else {
                    setError('Chat connection failed after multiple attempts. Please refresh the page.');
                }
            };
        };
        connectWebSocket(); // Initiate WebSocket connection
        // Cleanup function for useEffect: closes WebSocket when component unmounts or dependencies change
        return () => {
            if (ws) {
                ws.close(); // Close WebSocket connection if it exists
            }
        };
    }, [user, isLoading, slug, navigate]); // Effect dependencies: rerun if user, loading state, slug, or navigate changes
// -----------------------------
// Function to send a chat message
// -----------------------------
    const sendMessage = () => {
        // Check if WebSocket exists
        if (!ws) {
            setError('Chat is disconnected. Please refresh the page.');
            return;
        }
        // Check if message is empty
        if (!message.trim()) {
            setError('Message cannot be empty.');
            return;
        }
        // Check if WebSocket is open
        if (ws.readyState !== WebSocket.OPEN) {
            setError('Chat is not connected. Retrying...');
            return;
        }
        // Check if user exists
        if (!user) {
            setError('You must be logged in to send messages.');
            return;
        }
        setIsSending(true); // Mark as sending
        const payload = JSON.stringify({message: message.trim(), username: user.username}); // Prepare message payload
        try {
            ws.send(payload); // Send message over WebSocket
            setMessage(''); // Clear input field
            lastSentMessage.current = `${user.username}:${message.trim()}:${new Date().toISOString()}`; // Save last sent message
        } catch (err) {
            console.error('ChatPage: Error sending message=', err);
            setError('Failed to send message. Please try again.'); // Show error if send fails
        } finally {
            setIsSending(false); // Reset sending state
        }
    };
// -----------------------------
// Handle key press in chat input
// -----------------------------
    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault(); // Prevent default Enter behavior (like new line)
            sendMessage(); // Trigger sendMessage
        }
    };
// -----------------------------
// Handle Send button click
// -----------------------------
    const handleSendClick = () => {
        if (!isSending) {
            sendMessage(); // Send message if not already sending
        }
    };
// -----------------------------
// Toggle collapse state of chat window
// -----------------------------
    const toggleCollapse = () => {
        setIsCollapsed(!isCollapsed); // Invert current collapse state
    };
// -----------------------------
// Show loading spinner if auth is still loading
// -----------------------------
    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="animate-spin rounded-full h-12 w-12 border-t-4 border-blue-600"></div>
            </div>
        );
    }
// If no user, return null
    if (!user) return null;
// -----------------------------
// Modernized Main Chat UI
// -----------------------------
return (
  <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 font-sans">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12 max-w-3xl">

      {/* Navigation */}
      <div className="mb-8 flex flex-wrap gap-4">
        <button
          onClick={() => navigate(-1)}
          className="px-5 py-2 bg-blue-600 text-white font-medium rounded-lg shadow hover:bg-blue-700 transition"
        >
          Back
        </button>
        <button
          onClick={() => navigate('/')}
          className="px-5 py-2 bg-blue-600 text-white font-medium rounded-lg shadow hover:bg-blue-700 transition"
        >
          Back to Homepage
        </button>
      </div>

      {/* Chat container */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200">

        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 flex justify-between items-center">
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight">💬 {courseName} Chat</h1>
          <button
            onClick={toggleCollapse}
            className="text-white hover:text-gray-200 transition"
            aria-label={isCollapsed ? 'Expand chat' : 'Collapse chat'}
          >
            <svg
              className={`w-6 h-6 transform transition-transform ${isCollapsed ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
            </svg>
          </button>
        </div>

        {/* Chat content */}
        {!isCollapsed && (
          <>
            {/* Error */}
            {error && (
              <div className="p-4 bg-red-50 text-red-700 border-t border-red-200">
                {error}
              </div>
            )}

            {/* Messages */}
            <div className="p-4 max-h-[28rem] overflow-y-auto bg-gray-50 space-y-3 scroll-smooth">
              {messages.length === 0 ? (
                <p className="text-gray-500 text-center italic">No chat history yet. Start the conversation!</p>
              ) : (
                messages.map((msg, index) => {
                  const isUser = msg.username === user.username;
                  return (
                    <div
                      key={index}
                      className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`rounded-xl px-4 py-2 shadow-sm max-w-[75%] ${
                          isUser
                            ? 'bg-blue-100 text-gray-900'
                            : 'bg-gray-200 text-gray-800'
                        }`}
                      >
                        <p className="font-semibold text-sm">{msg.username}</p>
                        <p className="mt-1 break-words">{msg.message}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(msg.timestamp.replace(' ', 'T') + 'Z').toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="p-3 bg-gray-100 border-t">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="flex-grow px-3 py-2 border rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Type a message..."
                  aria-label="Chat input"
                  disabled={isSending}
                />
                <button
                  onClick={handleSendClick}
                  disabled={isSending}
                  className={`px-5 py-2 rounded-lg shadow text-white transition-colors ${
                    isSending
                      ? 'bg-blue-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                  aria-label="Send message"
                >
                  {isSending ? '...' : 'Send'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  </div>
);

}
export default ChatPage; // Export ChatPage component as default
